import {
  House, Users,
  Calendar, Layers, ShoppingCart,
  MessageSquare, GitBranch, Megaphone, BarChart3,
  Settings, type LucideIcon
} from 'lucide-react'
import type { CapabilityName } from '@/lib/permissions'
import type { PhaseTone } from '@/lib/phaseTone'

export interface NavItem {
  label: string
  path: string
  icon: LucideIcon
  /** Colour of the row's icon tile (wayfinding, like iOS Settings; not a phase). */
  tone: PhaseTone | 'rose' | 'neutral'
  badge?: number
  /** Items with the same group sit together; a gap separates groups. */
  group: 'day' | 'work' | 'grow' | 'admin'
  /**
   * What the person must be able to do to see this item. Hidden otherwise,
   * so a crew member never taps into a "You don't have access" page.
   * Omit for items everyone sees (Home; Settings shows personal sections
   * to everyone).
   */
  capability?: CapabilityName
}

// Grouped by how the day runs: what's happening (Home, Inbox, Calendar),
// the work itself (Work, Contacts), then growing the business. The desktop
// sidebar puts the 'admin' group (Settings) in its footer.
export const navItems: NavItem[] = [
  { label: 'Home',         path: '/dashboard',    icon: House,         tone: 'neutral', group: 'day' },
  { label: 'Inbox',        path: '/inbox',        icon: MessageSquare, tone: 'sky', group: 'day', capability: 'viewInbox' },
  { label: 'Calendar',     path: '/calendar',     icon: Calendar,      tone: 'blush', group: 'day', capability: 'viewCalendar' },
  { label: 'Work',         path: '/work',         icon: Layers,        tone: 'peach', group: 'work', capability: 'viewWork' },
  { label: 'Purchasing',   path: '/purchasing',   icon: ShoppingCart,  tone: 'butter', group: 'work', capability: 'viewCosting' },
  // Contacts: people and companies. Login crew stays in Settings → Team. The
  // route stays /customers so links already out there keep working.
  { label: 'Contacts',     path: '/customers',    icon: Users,         tone: 'lavender', group: 'work', capability: 'viewContacts' },
  { label: 'Workflows',    path: '/workflows',    icon: GitBranch,     tone: 'butter', group: 'grow', capability: 'viewWorkflows' },
  { label: 'Marketing',    path: '/marketing',    icon: Megaphone,     tone: 'rose', group: 'grow', capability: 'viewMarketing' },
  { label: 'Reports',      path: '/reports',      icon: BarChart3,     tone: 'mint', group: 'grow', capability: 'viewReports' },
  { label: 'Settings',     path: '/settings',     icon: Settings,      tone: 'neutral', group: 'admin' },
]

/** The nav items this person can open, in order. */
export function visibleNavItems<T extends { capability?: CapabilityName }>(
  items: readonly T[],
  can: (c: CapabilityName) => boolean,
): T[] {
  return items.filter((i) => !i.capability || can(i.capability))
}

/**
 * Settings sections → what you need to see them (F1 keys). Personal sections
 * (profile, notifications, security) are open to everyone. Used by the
 * Settings page list and by the shell to decide whether any admin section
 * exists for this person.
 */
export const SETTINGS_SECTION_CAPABILITY: Record<string, CapabilityName | undefined> = {
  profile: undefined,
  notifications: undefined,
  security: undefined,
  usage: 'viewUsage', // spend is money: not for Crew
  availability: 'schedule.view', // your own hours + calendar privacy: personal, Crew included
  calendar: 'schedule.view',
  business: 'settings.business',
  brand: 'settings.business',
  money: 'settings.money',
  booking: 'settings.business',
  pricing: 'pricebook.manage',
  team: 'settings.team',
  // Line, calls, texting and quiet hours are tabs of this one page
  // (/settings/phone/<tab>); old /settings/a2p, /twilio and /services redirect.
  phone: 'settings.phone',
  integrations: 'settings.integrations',
  api: 'settings.integrations',
}

export function canSeeSettingsSection(id: string, can: (c: CapabilityName) => boolean): boolean {
  const cap = SETTINGS_SECTION_CAPABILITY[id]
  return !cap || can(cap)
}

/** The phone dock (BottomTabBar) tabs, gated the same way as the sidebar. */
export const DOCK_TAB_CAPABILITY: Record<string, CapabilityName | undefined> = {
  '/work': 'viewWork',
  '/customers': 'viewContacts',
  '/dashboard': undefined,
  '/inbox': 'viewInbox',
  '/calendar': 'viewCalendar',
}
